/**
 * `/konto/google`: Google's consent page sends the browser here with `code` and `state`, or
 * `error`. `public/oauth-return.js` has already lifted them off the address before the app booted;
 * this takes them once, matches the state against the stash, sends the code once, and puts the
 * trainer back on `/konto?tab=integracje` with the outcome (calendar-sync-requirements R3, R10).
 * The code is never logged, stored or put in an address.
 */

import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { ApiError } from '~/shared/api/mutator';
import { postMeCalendarConnectionsGoogle } from '~/shared/api/generated/endpoints/my-google-calendar-connection/my-google-calendar-connection';
import { takeOAuthReturn } from '~/shared/lib/oauthReturn';
import {
  forgetGoogleState,
  googleCalendarQueryKey,
  googleCalendarQueryOptions,
  googleReturnPage,
  readGoogleState,
} from '~/entities/google-calendar-connection';

import {
  connectAnswerOutcome,
  connectFailureOutcome,
  googleCalendarView,
  googleReturnStep,
  type GoogleCalendarView,
  type GoogleConnectOutcome,
} from '../model/googleCalendar';
import { accountTabSearch, type AccountTab } from '../model/accountTabs';

/** @returns a tab press on the return page's strip: leaves now, and the answer still primes the read. */
export function useGoogleCalendarReturn(): (tab: AccountTab) => void {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const left = useRef(false);

  /* No cleanup, as in `useWebCheckout`: the ref survives StrictMode's remount, so the parameters
   * are taken once, exactly one POST is made, and its answer always lands. */
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const step = googleReturnStep({ ...takeOAuthReturn(), stashed: readGoogleState() });
    forgetGoogleState();

    const finish = (outcome: GoogleConnectOutcome | null) => {
      if (left.current) return;
      void navigate({
        to: '/konto',
        search: outcome === null ? { tab: 'integracje' } : { tab: 'integracje', google: outcome },
        replace: true,
      });
    };
    if (step.kind !== 'post') {
      finish(step.kind === 'failed' ? 'failed' : null);
      return;
    }

    const send = async (): Promise<GoogleConnectOutcome> => {
      try {
        const answer = await postMeCalendarConnectionsGoogle({
          code: step.code,
          state: step.state,
          redirectUri: googleReturnPage(),
        });
        await queryClient.cancelQueries({ queryKey: googleCalendarQueryKey });
        queryClient.setQueryData(googleCalendarQueryKey, answer);
        return connectAnswerOutcome(googleCalendarView(answer));
      } catch (error) {
        // A code works once: the read is asked again before a failure is said.
        const reread: GoogleCalendarView | null = await queryClient
          .fetchQuery({ ...googleCalendarQueryOptions(), staleTime: 0 })
          .then(googleCalendarView, () => null);
        return connectFailureOutcome(error instanceof ApiError ? error.code : undefined, reread);
      }
    };
    void send().then(finish);
  }, [navigate, queryClient]);

  return (tab) => {
    left.current = true;
    void navigate({ to: '/konto', search: () => accountTabSearch(tab), replace: true });
  };
}
